import { $ } from "./core";
import { audioInit } from "./audio";
import { isOnchainConfigured } from "./chain/config";
import { capturePendingCode, fetchProgress, inviteLink, describeProgress, describeBar, type Progress } from "./chain/referral";

/* ================= referral program =================
   Terms: an invited player wagers ten times a Quick Hit card's price in ETH,
   and the person who invited them earns one free Quick Hit card. The free card
   plays at exactly the same odds as a paid one — an odds-boosted freebie is
   prohibited in most licensed markets.

   The gate is WAGERING, not deposits, and that is the whole design. Deposits
   are refundable, so a deposit gate generates no revenue to pay for the reward
   it unlocks; the previous terms (deposit $10, play two $1 cards) made the
   reward worth about five times the revenue that earned it, and made referring
   yourself profitable in expectation. Ten times the card price is where a
   self-referral stops paying.

   Codes and progress live on the relayer. They cannot live here: a referral is
   a claim about two people made by neither of them, and the scaffold this
   replaces minted a fresh code on every page load and tracked progress in one
   in-memory object nobody else could see. */
(function () {
  const modal = $("refModal");
  if (!modal) return;

  capturePendingCode();

  let progress: Progress | null = null;
  let link = "";

  function account(): string | null {
    const oc = (window as any).__onchain;
    return (oc && oc.account && oc.account()) || null;
  }

  function paint() {
    const state = $("refState");
    const linkEl = $("refLink");
    if (!state || !linkEl) return;

    if (!isOnchainConfigured()) {
      linkEl.textContent = "—";
      state.textContent = "Referrals need a live contract.";
      return;
    }
    if (!account()) {
      linkEl.textContent = "—";
      state.textContent = "Connect a wallet to get your invite link.";
      return;
    }
    if (!progress) {
      linkEl.textContent = "…";
      state.textContent = "Loading your invite link…";
      return;
    }

    link = progress.code ? inviteLink(progress.code) : "";
    linkEl.textContent = link || "—";

    /* The bar is read from the chain by the relayer, so the copy states the
       real number rather than a figure that could drift from a repriced tier. */
    const bar = $("refBar");
    if (bar) bar.textContent = describeBar(progress);

    /* Both sides of the program, because a player can be in both at once. */
    const lines: string[] = [];
    if (progress.invitedBy) lines.push(`Invited by ${progress.invitedBy} · ${describeProgress(progress)}`);
    if (progress.referrals > 0) {
      lines.push(
        `${progress.referrals} invited · ${progress.rewardsEarned} free ${progress.rewardsEarned === 1 ? "card" : "cards"} earned`
      );
    }
    if (!lines.length) lines.push("Share your link — you earn a free card when someone you invite plays.");
    state.textContent = lines.join("\n");
  }

  async function refresh() {
    const acct = account();
    if (!acct || !isOnchainConfigured()) {
      paint();
      return;
    }
    progress = await fetchProgress(acct);
    paint();
  }

  $("refOpen").onclick = () => {
    audioInit();
    paint();
    modal.classList.add("on");
    void refresh();
  };
  $("refClose").onclick = () => modal.classList.remove("on");
  modal.addEventListener("click", e => {
    if (e.target === modal) modal.classList.remove("on");
  });

  $("refCopy").onclick = async () => {
    if (!link) return;
    const btn = $("refCopy");
    try {
      await navigator.clipboard.writeText(link);
      btn.textContent = "✓ Copied";
    } catch (e) {
      btn.textContent = "Copy failed — select the link";
    }
    setTimeout(() => (btn.textContent = "Copy invite link"), 1600);
  };

  const share = $("refShare");
  if (share && (navigator as any).share) {
    share.style.display = "";
    share.onclick = () => {
      if (!link) return;
      (navigator as any)
        .share({ title: "Scratchy", text: "Try Scratchy with my invite", url: link })
        .catch(() => {});
    };
  }

  /* Kept for core-game, which calls these on every deposit and paid play. The
     counting itself moved to the relayer — it is the only party that sees both
     sides — so these now just keep the panel fresh if it happens to be open. */
  (window as any).__ref = {
    get link() {
      return link;
    },
    onDeposit() {
      if (modal.classList.contains("on")) void refresh();
    },
    onPaidPlay() {
      if (modal.classList.contains("on")) void refresh();
    },
    refresh
  };

  const swBtn = $("shareWin");
  if (swBtn) {
    swBtn.onclick = async () => {
      const w = (window as any).__lastWin;
      if (!w || !link) return;
      const msg = `I just hit ${w.mult.toLocaleString()}x on Scratchy. ${link}`;
      try {
        if ((navigator as any).share) await (navigator as any).share({ title: "Scratchy", text: msg, url: link });
        else await navigator.clipboard.writeText(msg);
      } catch (e) {}
    };
  }
})();
